<template>
  <div class="container">
    <div class="row justify-content-center">
      <div class="col col-sm-10 col-md-8 col-lg-7 col-xl-6 col-xxl-6 col-xxxl-6">
        <div class="card border-0 rounded-0 bg-white authentication-card">
          <div class="card-body letter-spacing">
            <h4 class="fw-bold mb-0">{{ $t('feature.auth.request_support') }}</h4>
            <p class="mb-0 fs-md-15 fs-lg-16 text-paragraph sub-text">
              {{ $t('feature.auth.$request_support.message') }}
            </p>
            <form v-on:submit="submit">
              <!-- EMAIL -->
              <div class="form-group mb-15 mb-sm-20">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('form.email.label') }}
                </label>
                <input type="email" :title="$t('form.email.label')" class="form-control shadow-none text-black"
                  v-model="form.email"
                  :class="[{ 'is-invalid': submitted && !email_is_valid(form.email) && errors?.email }]"
                  :placeholder="$t('form.email.placeholder')" required pattern="[^@\s]+@[^@\s]+\.[^@\s]+" />
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.email ?? false">
                  <p v-for="error in errors.email" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                    error }}</p>
                </div>
              </div>
              <!-- FIRST NAME -->
              <div class="form-group mb-15 mb-sm-20">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('form.first_name.label') }}
                </label>
                <input :title="$t('form.first_name.label')" class="form-control shadow-none text-black"
                  v-model="form.first_name"
                  :class="[{ 'is-invalid': submitted && !form.first_name && errors?.first_name }]"
                  :placeholder="$t('form.first_name.placeholder')" required />
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.first_name ?? false">
                  <p v-for="error in errors.first_name" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                    error }}</p>
                </div>
              </div>
              <!-- LAST NAME -->
              <div class="form-group mb-15 mb-sm-20">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('form.last_name.label') }}
                </label>
                <input :title="$t('form.last_name.label')" class="form-control shadow-none text-black"
                  v-model="form.last_name"
                  :class="[{ 'is-invalid': submitted && !form.last_name && errors?.last_name }]"
                  :placeholder="$t('form.last_name.placeholder')" required />
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.last_name ?? false">
                  <p v-for="error in errors.last_name" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                    error }}</p>
                </div>
              </div>
              <!-- REQUEST TYPE -->
              <div class="form-group mb-15 mb-sm-20">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('feature.auth.$request_support.request_type') }}
                </label>
                <select class="form-select shadow-none text-black fs-md-15" v-model="form.request_type" required
                  :class="[{ 'is-invalid': submitted && (!form.request_type || form.request_type === -1) && errors?.request_type }]">
                  <option value="" selected disabled>{{ $t('form.select_placeholder') }}</option>
                  <template v-if="labels">
                    <option v-for="label in labels" :value="label.id" :key="label.id">{{ label.description }}</option>
                  </template>
                </select>
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.request_type ?? false">
                  <p v-for="error in errors.request_type" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                    error }}</p>
                </div>
              </div>
              <!-- MESSAGE -->
              <div class="form-group mb-15 mb-sm-20">
                <label class="d-block text-black fw-semibold mb-10">
                  {{ $t('form.message.label') }}
                </label>
                <textarea :title="$t('form.message.label')" class="form-control shadow-none text-black"
                  v-model="form.message" :class="[{ 'is-invalid': submitted && !form.message && errors?.message }]"
                  :placeholder="$t('form.message.placeholder')" required rows="6"></textarea>
                <!-- ERRORS -->
                <div class="form-messages mt-2 px-1" v-if="errors?.message ?? false">
                  <p v-for="error in errors.message" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                    error }}</p>
                </div>
              </div>
              <button class="default-btn transition fw-medium fs-md-15 fs-lg-16 d-block w-100" type="submit"
                v-on:click="setPristine" v-if="!loading">
                {{ $t('cta.submit') }}
              </button>
              <SpinnerLoading v-else />
              <div class="text-center mt-15 mt-sm-20 mt-md-25">
                <router-link to="/login"
                  class="link-btn text-decoration-none fw-medium fs-md-15 fs-lg-16 position-relative">
                  <i class="ph-bold ph-arrow-left"></i>
                  {{ $t('cta.back_to_login') }}
                </router-link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    <!-- ERROR MESSAGE -->
    <div class="row justify-content-center">
      <div class="col col-sm-10 col-md-8 col-lg-7 col-xl-6 col-xxl-6 col-xxxl-6">
        <div class="alert alert-danger alert-dismissible fade border-0 border-danger-subtle border-top rounded-0 m-0"
          role="alert" id="errorMessage">
          <strong>{{ $t('error.something_went_wrong') }}</strong>
          <button type="button" class="btn-close" v-on:click="hideError" aria-label="Close"></button>
        </div>
      </div>
    </div>
  </div>

  <AlertModal id="alertModal" :title="$t('feature.auth.request_support')" :dismissible="false"
    :message="$t('feature.auth.$request_support.success_message')" @confirm="redirect" />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import API from '../../../services/api/api.service';
import { Alert, Modal } from "bootstrap";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import { email_is_valid, request_support_form, RequestSupportForm } from '@/utils/methods/form_utils';
import { getInstance, ReCaptchaInstance } from 'recaptcha-v3';
import { Label } from '@/utils/models/label.model';
import AlertModal from '@/components/Widgets/AlertModal.vue';

export default defineComponent({
  name: "ForgotPassword",
  components: {
    SpinnerLoading,
    AlertModal,
  },
  data: function () {
    return {
      loading: false,
      form: { ...request_support_form },
      request_types: [1, 2, 3],
      submitted: false,
      errorMessage: undefined,
      recaptcha: undefined,
      labels: undefined,
      errors: undefined,
      alertModal: null,
    } as {
      loading: boolean,
      form: RequestSupportForm,
      request_types: number[],
      submitted: boolean,
      errorMessage?: Alert,
      recaptcha?: ReCaptchaInstance,
      labels?: Label[],
      errors?: any,
      alertModal: Modal | null,
    }
  },
  async mounted() {
    this.errorMessage = new Alert('#errorMessage');
    this.alertModal = new Modal('#alertModal', {});
    this.recaptcha = await getInstance();
    this.recaptcha?.showBadge();
    this.getRequestTypes();
  },
  beforeUnmount() {
    this.recaptcha?.hideBadge();
  },
  computed: {
  },
  methods: {
    email_is_valid(email?: string) {
      return email_is_valid(email);
    },
    getRequestTypes() {
      API.getLabels({ label: 'supports', lang: this.$i18n.locale }).then((response) => {
        if (response.data.success === true) {
          this.labels = response.data.data;
        }
      });
    },
    submit(e) {
      this.submitted = true;
      e?.preventDefault();
      this.sendRequest();
    },
    async sendRequest() {
      this.errors = undefined;
      this.loading = true;
      this.form['g-recaptcha-response'] = await this.recaptcha?.execute('support_request');
      API.getSupport(this.form).then((response) => {
        if (response.data.success === true) {
          const info = response.data.data;
          this.alertModal?.show();
        }
      }).catch((error) => {
        this.errors = error.response?.data?.errors;
        this.showError();
      }).finally(() => {
        this.loading = false;
      });
    },
    setPristine() {
      this.submitted = true;
    },
    showError() {
      const error = document.getElementById('errorMessage');
      error?.classList.add('show');
      setTimeout(() => {
        this.hideError();
      }, 5000)
    },
    hideError() {
      const error = document.getElementById('errorMessage');
      error?.classList.remove('show');
    },
    redirect() {
      this.$router.push('/login');
    },
  }
});


</script>